import { Box, Typography, Button, Grid, Card, CardContent, Chip } from '@mui/material';
import {
  BusinessOutlined as BusinessIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ContentCopy as ContentIcon,
} from '@mui/icons-material';
import EmptyProfilesState from './EmptyProfilesState';

interface BusinessProfilesListProps {
  profiles: any[];
  onCreateProfile: () => void;
  onEditProfile: (id: number) => void;
  onViewProfile: (id: number) => void;
  onGenerateContent: (id: number) => void;
}

const BusinessProfilesList: React.FC<BusinessProfilesListProps> = ({
  profiles,
  onCreateProfile,
  onEditProfile,
  onViewProfile,
  onGenerateContent,
}) => {
  if (profiles.length === 0) {
    return <EmptyProfilesState onCreateProfile={onCreateProfile} />;
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Your Business Profiles
        </Typography>
        <Button
          variant="outlined"
          startIcon={<BusinessIcon />}
          onClick={onCreateProfile}
        >
          Create New Profile
        </Button>
      </Box>

      <Grid container spacing={3}>
        {profiles.map((profile) => (
          <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={profile.id}>
            <Card sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)',
              },
            }}>
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {profile.business_name}
                  </Typography>
                  <Chip
                    label={profile.business_type?.name || 'Business'}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Box>

                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 3,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {profile.description || 'No description provided'}
                </Typography>

                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                    Platforms: {profile.preferred_platform_ids?.length || 0} selected
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => onViewProfile(profile.id)}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => onEditProfile(profile.id)}
                      variant="outlined"
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ContentIcon />}
                      onClick={() => onGenerateContent(profile.id)}
                      variant="contained"
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      Generate
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default BusinessProfilesList;
